import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  request,
  type V3ApiFailedResponse,
  type V3ApiSuccessfulResponse,
} from 'javascripts/services/v3ApiClient';
import type { ProjectDatabaseId } from 'data/v1';
import type {
  TaskObject,
  TaskImageObject,
  TaskVideoObject,
} from 'data/v3/data-contracts';
import {
  GetProjectTasksResponse,
  PROJECT_TASKS_QUERY_KEY,
} from './useGetProjectTasks';
import {
  GetAssetProjectTasksResponse,
  ASSET_PROJECT_TASKS_QUERY_KEY,
} from './useGetAssetProjectTasks';

type DeleteProjectTaskResponse = V3ApiSuccessfulResponse<{
  message: string;
}>;

export const useDeleteProjectTask = ({
  taskDbId,
  projectDbId,
  assetDbId,
  onError,
  onSuccess,
}: {
  taskDbId: TaskObject['id'];
  projectDbId: ProjectDatabaseId;
  assetDbId: TaskImageObject['id'] | TaskVideoObject['id'];
  onSuccess?: () => void;
  onError?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation<DeleteProjectTaskResponse, V3ApiFailedResponse>({
    mutationFn: () => {
      return request({
        url: `/projects/${projectDbId}/tasks/${taskDbId}`,
        method: 'delete',
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [PROJECT_TASKS_QUERY_KEY, projectDbId],
      });

      const data = queryClient.getQueryData<GetProjectTasksResponse>([
        PROJECT_TASKS_QUERY_KEY,
        projectDbId,
      ]);

      if (data?.data) {
        const existingTasks = data.data.tasks || [];
        const tasksCount = data.data.tasksCount || 0;

        queryClient.setQueryData([PROJECT_TASKS_QUERY_KEY, projectDbId], {
          data: {
            ...data,
            tasks: existingTasks.filter(
              (task: TaskObject) => task.id !== taskDbId,
            ),
            tasksCount: tasksCount - 1,
          },
        });
      }

      // Update cache for asset project task
      if (assetDbId) {
        await queryClient.cancelQueries({
          queryKey: [ASSET_PROJECT_TASKS_QUERY_KEY, assetDbId],
        });

        const assetProjectTaskData =
          await queryClient.getQueryData<GetAssetProjectTasksResponse>([
            ASSET_PROJECT_TASKS_QUERY_KEY,
            assetDbId,
          ]);

        if (assetProjectTaskData) {
          queryClient.setQueryData([ASSET_PROJECT_TASKS_QUERY_KEY, assetDbId], {
            data: assetProjectTaskData.data.filter(
              (task: TaskObject) => task.id !== taskDbId,
            ),
          });
        }
      }
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [PROJECT_TASKS_QUERY_KEY, projectDbId],
      });
    },
  });
};
